import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import NetInfo from 'app/services/IsolatedNetInfo';
import type { WherebyWebView } from '@whereby.com/react-native-sdk/embed';
import { isNetworkSignalPoor } from '../utils/deviceSignals';

export const CALL_QUALITY_RECOVERY_MS = 8_000;

type TConnectionStatusPayload = { status: 'stable' | 'unstable' };
type TCameraTogglePayload = { enabled: boolean };

type TAudioOnlyFallback = {
    isAudioOnly: boolean;
    handleConnectionStatusChange: (payload: TConnectionStatusPayload) => void;
    handleCameraToggle: (payload: TCameraTogglePayload) => void;
};

export const useAudioOnlyOnPoorConnection = (
    wherebyRef: RefObject<WherebyWebView | null>,
): TAudioOnlyFallback => {
    const [isAudioOnly, setIsAudioOnly] = useState(false);
    const isUnstable = useRef(false);
    const isSignalPoor = useRef(false);
    const isCameraEnabled = useRef(true);
    const wasCameraDisabledAutomatically = useRef(false);
    const pendingProgrammaticToggle = useRef<boolean | null>(null);
    const recoveryTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const requestCamera = useCallback((enabled: boolean) => {
        pendingProgrammaticToggle.current = enabled;
        isCameraEnabled.current = enabled;
        wherebyRef.current?.toggleCamera(enabled);
    }, [wherebyRef]);

    const applyConnectionQuality = useCallback(() => {
        if (isSignalPoor.current || isUnstable.current) {
            if (recoveryTimeout.current !== null) {
                clearTimeout(recoveryTimeout.current);
                recoveryTimeout.current = null;
            }
            if (isCameraEnabled.current) {
                requestCamera(false);
                wasCameraDisabledAutomatically.current = true;
            }
            setIsAudioOnly(true);
            return;
        }

        if (recoveryTimeout.current !== null) return;
        recoveryTimeout.current = setTimeout(() => {
            recoveryTimeout.current = null;
            if (wasCameraDisabledAutomatically.current) {
                wasCameraDisabledAutomatically.current = false;
                requestCamera(true);
            }
            setIsAudioOnly(false);
        }, CALL_QUALITY_RECOVERY_MS);
    }, [requestCamera]);

    const handleConnectionStatusChange = useCallback(({ status }: TConnectionStatusPayload) => {
        isUnstable.current = status === 'unstable';
        applyConnectionQuality();
    }, [applyConnectionQuality]);

    const handleCameraToggle = useCallback(({ enabled }: TCameraTogglePayload) => {
        isCameraEnabled.current = enabled;

        if (pendingProgrammaticToggle.current === enabled) {
            pendingProgrammaticToggle.current = null;
            return;
        }

        pendingProgrammaticToggle.current = null;
        if (enabled) {
            wasCameraDisabledAutomatically.current = false;
            setIsAudioOnly(false);
        }
    }, []);

    useEffect(() => {
        const unsubscribe = NetInfo.addEventListener((state) => {
            isSignalPoor.current = isNetworkSignalPoor(state);
            applyConnectionQuality();
        });

        return () => {
            unsubscribe();
            if (recoveryTimeout.current !== null) clearTimeout(recoveryTimeout.current);
        };
    }, [applyConnectionQuality]);

    return { isAudioOnly, handleConnectionStatusChange, handleCameraToggle };
};
